import type L from "leaflet";
import type { MapObject } from "@fortex/api";
import type { CatalogueEntry, LegendEntry } from "../catalogue/types";
import type { Labels } from "../i18n/labels";
import type { SourceFactory } from "../sources/types";

export type LatLngTuple = [number, number];

export type BoundsTuple = [LatLngTuple, LatLngTuple];

export type DrawMode = "point" | "polyline" | "polygon";

export type Theme = "light" | "dark" | "system";

export type Chrome = "full" | "compact" | "none";

export type Density = "comfortable" | "compact";

export type ObjectAction = {
  id: string;
  label: string;
  icon?: string;
  primary?: boolean;
  run: (object: MapObject) => void;
};

export type HostLayerDefinition = {
  key: string;
  title: string;
  group?: string;
  kind?: "geojson" | "leaflet" | "track";
  geojson?: GeoJSON.GeoJSON;
  layer?: L.Layer;
  track?: { objectId: number; from?: Date | null; to?: Date | null; color?: string | null; playback?: boolean };
  color?: string | null;
  legend?: LegendEntry[] | null;
  selected?: boolean;
  fitBounds?: () => L.LatLngBounds | null;
};

export type ForteXMapConfig = {
  lang?: "hu" | "en";
  langId?: number;
  theme?: Theme;
  chrome?: Chrome;
  density?: Density;
  accent?: string | null;
  storageKey?: string | null;
  urlState?: boolean;
  view?: {
    center?: LatLngTuple;
    zoom?: number;
    minZoom?: number;
    maxZoom?: number;
    fitTo?: BoundsTuple | "content" | null;
  };
  layers?: {
    defaultOn?: string[];
    hidden?: string[];
    maps?: string[] | null;
    layersOffered?: string[] | null;
    extra?: HostLayerDefinition[];
    group?: boolean;
  };
  selection?: { enabled?: boolean; multi?: boolean; box?: boolean };
  draw?: { mode?: DrawMode | null; editable?: boolean; snap?: boolean; wkt?: string | null; points?: LatLngTuple[]; fit?: boolean };
  info?: { enabled?: boolean; admin?: boolean | null; pictures?: boolean; layers?: string[] | null; onObjectActions?: ((object: MapObject) => ObjectAction[]) | null };
  controls?: {
    rail?: boolean;
    drawer?: boolean;
    search?: boolean | { objects?: boolean; address?: boolean; layers?: string[] | null; label?: string | null };
    zoom?: boolean;
    scale?: boolean;
    coordinates?: boolean;
    locate?: boolean;
    fullscreen?: boolean;
    measure?: boolean;
    fit?: boolean;
    miniMap?: boolean;
    drawToolbar?: boolean;
    hint?: boolean;
    attribution?: boolean;
    legend?: boolean;
    table?: boolean;
    route?: boolean;
    layersTab?: boolean;
  };
  sources?: Record<string, SourceFactory>;
  labels?: Partial<Labels>;
  nominatimUrl?: string | null;
  nominatimCountry?: string | null;
};

export type ResolvedOptions = {
  lang: "hu" | "en";
  langId: number;
  theme: Theme;
  chrome: Chrome;
  density: Density;
  accent: string | null;
  storageKey: string | null;
  urlState: boolean;
  view: { center: LatLngTuple; zoom: number; minZoom: number; maxZoom: number; fitTo: BoundsTuple | "content" | null };
  layers: { defaultOn: string[]; hidden: string[]; maps: string[] | null; layersOffered: string[] | null; extra: HostLayerDefinition[]; group: boolean };
  selection: { enabled: boolean; multi: boolean; box: boolean };
  draw: { mode: DrawMode | null; editable: boolean; snap: boolean; wkt: string | null; points: LatLngTuple[]; fit: boolean };
  info: { enabled: boolean; admin: boolean | null; pictures: boolean; layers: string[] | null; onObjectActions: ((object: MapObject) => ObjectAction[]) | null };
  controls: {
    rail: boolean;
    drawer: boolean;
    search: { enabled: boolean; objects: boolean; address: boolean; layers: string[] | null; label: string | null };
    zoom: boolean;
    scale: boolean;
    coordinates: boolean;
    locate: boolean;
    fullscreen: boolean;
    measure: boolean;
    fit: boolean;
    miniMap: boolean;
    drawToolbar: boolean;
    hint: boolean;
    attribution: boolean;
    legend: boolean;
    table: boolean;
    route: boolean;
    layersTab: boolean;
  };
  sources: Record<string, SourceFactory>;
  labels: Partial<Labels>;
};

export const defaultCenter: LatLngTuple = [46.9067, 19.6913];
export const defaultZoom = 13;
export const focusZoom = 15;

export function resolveOptions(config: ForteXMapConfig | undefined): ResolvedOptions {
  const c = config ?? {};
  const lang = c.lang ?? (c.langId === 2 ? "en" : "hu");
  const search = c.controls?.search;
  const chrome = c.chrome ?? "full";
  const compact = chrome === "compact";

  return {
    lang,
    langId: c.langId ?? (lang === "en" ? 2 : 1),
    theme: c.theme ?? "system",
    chrome,
    density: c.density ?? "comfortable",
    accent: c.accent ?? null,
    storageKey: c.storageKey ?? null,
    urlState: c.urlState ?? false,
    view: {
      center: c.view?.center ?? defaultCenter,
      zoom: c.view?.zoom ?? defaultZoom,
      minZoom: c.view?.minZoom ?? 3,
      maxZoom: c.view?.maxZoom ?? 21,
      fitTo: c.view?.fitTo ?? null
    },
    layers: {
      defaultOn: c.layers?.defaultOn ?? [],
      hidden: c.layers?.hidden ?? [],
      maps: c.layers?.maps ?? null,
      layersOffered: c.layers?.layersOffered ?? null,
      extra: c.layers?.extra ?? [],
      group: c.layers?.group ?? true
    },
    selection: { enabled: c.selection?.enabled ?? true, multi: c.selection?.multi ?? true, box: c.selection?.box ?? true },
    draw: {
      mode: c.draw?.mode ?? null,
      editable: c.draw?.editable ?? true,
      snap: c.draw?.snap ?? true,
      wkt: c.draw?.wkt ?? null,
      points: c.draw?.points ?? [],
      fit: c.draw?.fit ?? true
    },
    info: { enabled: c.info?.enabled ?? true, admin: c.info?.admin ?? null, pictures: c.info?.pictures ?? true, layers: c.info?.layers ?? null, onObjectActions: c.info?.onObjectActions ?? null },
    controls: {
      rail: c.controls?.rail ?? chrome === "full",
      drawer: c.controls?.drawer ?? chrome !== "none",
      search: {
        enabled: search === false ? false : (c.controls?.search === undefined ? chrome === "full" : true),
        objects: typeof search === "object" ? search.objects ?? true : true,
        address: typeof search === "object" ? search.address ?? true : true,
        layers: typeof search === "object" ? search.layers ?? null : null,
        label: typeof search === "object" ? search.label ?? null : null
      },
      zoom: c.controls?.zoom ?? chrome !== "none",
      scale: c.controls?.scale ?? chrome === "full",
      coordinates: c.controls?.coordinates ?? chrome === "full",
      locate: c.controls?.locate ?? chrome === "full",
      fullscreen: c.controls?.fullscreen ?? chrome === "full",
      measure: c.controls?.measure ?? chrome === "full",
      fit: c.controls?.fit ?? chrome !== "none",
      miniMap: c.controls?.miniMap ?? false,
      drawToolbar: c.controls?.drawToolbar ?? false,
      hint: c.controls?.hint ?? !compact,
      attribution: c.controls?.attribution ?? true,
      legend: c.controls?.legend ?? chrome === "full",
      table: c.controls?.table ?? true,
      route: c.controls?.route ?? true,
      layersTab: c.controls?.layersTab ?? true
    },
    sources: c.sources ?? {},
    labels: c.labels ?? {}
  };
}

export function entryIsOffered(entry: CatalogueEntry, options: ResolvedOptions): boolean {
  if (options.layers.hidden.includes(entry.key) || options.layers.hidden.includes(entry.idName)) return false;

  const offered = entry.root === "maps" ? options.layers.maps : options.layers.layersOffered;

  if (offered === null) return true;

  return offered.includes(entry.key) || offered.includes(entry.idName);
}
